/**
 * The deposit release panel — the screen where the deposit is actually decided.
 *
 * One component, two audiences. The lessor's representative sees it in German at
 * /deposit-release/:token with no account; an admin sees it in English inside the
 * tenant profile when recording a decision made by phone. Every string goes
 * through `t()` so that stays a prop rather than a fork.
 *
 * Money is never computed here. Every mutation returns the server's recomputed
 * refund, because the database derives it from the deduction rows and a second
 * opinion in the browser is exactly the disagreement that would end up in front
 * of a tenant. The running total shown while typing an unsaved line is labelled
 * as a preview for the same reason.
 *
 * A partial release cannot be submitted until every deduction carries a written
 * reason — enforced here for a usable message, and again by a CHECK constraint so
 * an unexplained line cannot exist at all.
 */

import { useMemo, useState } from "react";
import {
  addDeduction,
  deleteDeduction,
  releaseDeposit,
  releaseDepositByAmount,
  updateDeduction,
  type DeductionCategory,
  type DepositReleaseLogEvent,
  type ReleaseAuth,
  type ReleaseDecision,
  type ReleaseReviewState,
} from "@/lib/depositReleaseApi";

const REASON_MIN_LENGTH = 10;

const CATEGORIES: DeductionCategory[] = [
  "damage",
  "cleaning",
  "unpaid_rent",
  "fees",
  "other",
];

type Locale = "de" | "en";

const COPY = {
  title: { de: "Kaution freigeben", en: "Release the deposit" },
  intro: {
    de: "Prüfen Sie die Unterlagen unten und entscheiden Sie, wie viel von der Kaution zurückgezahlt wird — vollständig, teilweise mit Begründung, oder gar nicht. Mieter/in und Sie erhalten danach automatisch eine Abrechnung per E-Mail.",
    en: "Check the documents below, then decide how much of the deposit comes back — in full, partly with a written reason, or not at all. The tenant and you will both get a statement by email automatically once you submit.",
  },
  tenant: { de: "Mieter/in", en: "Tenant" },
  unit: { de: "Wohneinheit", en: "Unit" },
  tenancy: { de: "Mietzeit", en: "Tenancy" },
  bundleTitle: { de: "Unterlagen zum Auszug", en: "Move-out documents" },
  bundleSub: {
    de: "Mietvertrag, Einzugs- und Auszugsprotokoll in einer PDF zusammengefasst",
    en: "Contract, move-in and move-out protocol merged into one PDF",
  },
  openBundle: { de: "Öffnen", en: "Open" },
  agreed: { de: "Vereinbarte Kaution", en: "Deposit agreed at signing" },
  held: { de: "Gezahlte Kaution", en: "Deposit held" },
  shortfallTitle: {
    de: "Kaution nicht vollständig gezahlt",
    en: "Deposit underpaid",
  },
  shortfall: {
    de: "Einbehalte sind nur bis zur Höhe der gezahlten Kaution möglich. Der Differenzbetrag ist eine offene Forderung und kein Einbehalt.",
    en: "Deductions are capped at the amount actually paid. The difference is a separate receivable, not a deduction.",
  },
  question: { de: "Wie viel wird freigegeben?", en: "How much is released?" },
  modeAmount: { de: "Betrag eingeben", en: "Enter an amount" },
  modeItemised: { de: "Positionen erfassen", en: "Itemise" },
  refundAmount: { de: "Rückzahlungsbetrag", en: "Refund amount" },
  ofTotal: { de: "von", en: "of" },
  totalDeposit: { de: "gezahlter Kaution", en: "total deposit" },
  fullAmountBtn: { de: "Voller Betrag", en: "Full amount" },
  withholdAllBtn: { de: "Alles einbehalten", en: "Withhold all" },
  withheld: { de: "Einbehalt", en: "Withheld" },
  amountReason: {
    de: "Begründung für den Einbehalt",
    en: "Reason for the amount withheld",
  },
  amountReasonVisible: {
    de: "Wird dem Mieter/der Mieterin in der Kautionsabrechnung angezeigt.",
    en: "Shown to the tenant in the refund statement.",
  },
  internalNote: {
    de: "Interne Notiz (optional)",
    en: "Internal note (optional)",
  },
  internalNoteHint: {
    de: "Nur intern sichtbar — erscheint nicht in der Kautionsabrechnung.",
    en: "Admin-only — never appears in the tenant's statement.",
  },
  amountTooHigh: {
    de: "Die Rückzahlung kann nicht höher sein als die gezahlte Kaution.",
    en: "The refund cannot be more than the deposit held.",
  },
  amountInvalid: { de: "Bitte einen Betrag eingeben.", en: "Enter an amount." },
  hasItemised: {
    de: "Es sind bereits Positionen erfasst. Bitte über „Positionen erfassen“ freigeben.",
    en: "This settlement already has itemised deductions — release it from the itemised view.",
  },
  confirmAmount: { de: "Rückzahlung bestätigen", en: "Confirm refund amount" },
  documentsOnly: {
    de: "Dokumentiert die Rückzahlung — es wird keine Zahlung ausgelöst.",
    en: "This documents the refund only — no payment is triggered.",
  },
  fullTitle: { de: "Vollständig freigeben", en: "Release in full" },
  fullSub: {
    de: "Die gesamte Kaution geht an den Mieter/die Mieterin zurück. Keine Begründung nötig.",
    en: "The whole deposit goes back to the tenant. No reason needed.",
  },
  partialTitle: { de: "Teilweise freigeben", en: "Release part of it" },
  partialSub: {
    de: "Einbehalt je Position mit Betrag und schriftlicher Begründung.",
    en: "Each deduction needs an amount and a written reason.",
  },
  addLine: { de: "Position hinzufügen", en: "Add a deduction" },
  category: { de: "Kategorie", en: "Category" },
  reason: { de: "Begründung", en: "Reason" },
  reasonHint: {
    de: "Erscheint so in der Kautionsabrechnung des Mieters.",
    en: "Appears verbatim in the tenant's refund statement.",
  },
  reasonTooShort: {
    de: "Bitte begründen Sie den Einbehalt in einem Satz.",
    en: "Explain the deduction in a sentence.",
  },
  amount: { de: "Betrag", en: "Amount" },
  save: { de: "Speichern", en: "Save" },
  cancel: { de: "Abbrechen", en: "Cancel" },
  remove: { de: "Entfernen", en: "Remove" },
  deductions: { de: "Einbehalte", en: "Deductions" },
  refund: { de: "Rückzahlung an Mieter/in", en: "Refund to tenant" },
  preview: { de: "Vorschau", en: "preview" },
  overTitle: {
    de: "Einbehalte übersteigen die Kaution",
    en: "Deductions exceed the deposit",
  },
  over: {
    de: "Die Rückzahlung beträgt 0,00 €. Der übersteigende Betrag ist eine separate Forderung und wird nicht mit der Kaution verrechnet.",
    en: "The refund floors at zero. The excess is raised as a separate claim, not netted off.",
  },
  suggestionTitle: {
    de: "Vorschlag aus dem Mietkonto",
    en: "Suggested from the rent ledger",
  },
  suggestionApply: { de: "Übernehmen", en: "Use this" },
  suggestionDismiss: { de: "Verwerfen", en: "Dismiss" },
  submitFull: { de: "Vollständig freigeben", en: "Release in full" },
  submitPartial: { de: "Freigeben", en: "Release" },
  submitting: { de: "Wird gespeichert…", en: "Saving…" },
  needLine: {
    de: "Ein teilweiser Einbehalt braucht mindestens eine Position.",
    en: "A partial release needs at least one deduction.",
  },
  fullHasLines: {
    de: "Entfernen Sie die Einbehalte oder wählen Sie eine teilweise Freigabe.",
    en: "Remove the deductions, or choose a partial release.",
  },
  consequence: {
    de: "Mit dem Absenden erhält der Mieter/die Mieterin eine Kautionsabrechnung mit allen Positionen und Begründungen. Sie erhalten eine Kopie.",
    en: "Submitting sends the tenant a refund statement with every line and reason. You get a copy.",
  },
  doneTitle: { de: "Kaution freigegeben", en: "Deposit released" },
  doneBody: {
    de: "Vielen Dank. Die Kautionsabrechnung wurde erstellt.",
    en: "The refund statement has been produced.",
  },
  decidedBy: { de: "Entschieden von", en: "Decided by" },
} as const;

const CATEGORY_COPY: Record<DeductionCategory, { de: string; en: string }> = {
  damage: { de: "Schäden", en: "Damage" },
  cleaning: { de: "Reinigung", en: "Cleaning" },
  unpaid_rent: { de: "Mietrückstand", en: "Outstanding rent" },
  fees: { de: "Gebühren", en: "Fees" },
  other: { de: "Sonstiges", en: "Other" },
};

const CATEGORY_STYLE: Record<DeductionCategory, { bg: string; fg: string }> = {
  damage: { bg: "#FEE2E2", fg: "#B91C1C" },
  cleaning: { bg: "#E0E7FF", fg: "#4338CA" },
  unpaid_rent: { bg: "#FFEDD5", fg: "#C2410C" },
  fees: { bg: "#F1F5F9", fg: "#475569" },
  other: { bg: "#F1F5F9", fg: "#475569" },
};

/** German money for the representative, English for the admin. */
function money(cents: number, locale: Locale): string {
  const value = (Number(cents) || 0) / 100;
  return locale === "de"
    ? `${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : `€${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dateStr(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** "1234,56" or "1234.56" → cents. Accepts both separators. */
export function parseAmountToCents(raw: string): number | null {
  const cleaned = String(raw ?? "")
    .trim()
    .replace(/[€\s]/g, "")
    .replace(/\.(?=\d{3}\b)/g, "")
    .replace(",", ".");
  if (!cleaned || !/^\d*\.?\d*$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

interface DraftLine {
  category: DeductionCategory;
  reason: string;
  amount: string;
}

const emptyDraft = (): DraftLine => ({
  category: "damage",
  reason: "",
  amount: "",
});

export function DepositReleasePanel({
  auth,
  state,
  onState,
  onLog,
  locale = "de",
}: {
  auth: ReleaseAuth;
  state: ReleaseReviewState;
  onState: (next: ReleaseReviewState) => void;
  /** Told about every completed action, for the caller to audit-log — see DepositReleaseLogEvent. */
  onLog?: (event: DepositReleaseLogEvent) => void;
  locale?: Locale;
}) {
  const t = (key: keyof typeof COPY) => COPY[key][locale];

  const [decision, setDecision] = useState<ReleaseDecision | null>(
    state.deductions.length ? "partial" : null
  );
  const [draft, setDraft] = useState<DraftLine | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  /**
   * Amount mode is the default: most settlements are one number and one
   * sentence, and making that person build a table is friction for no gain.
   * A settlement that already carries itemised lines opens in itemised mode,
   * because the two ways of arriving at a withheld total must not fight.
   */
  const [mode, setMode] = useState<"amount" | "itemised">(
    state.deductions.length ? "itemised" : "amount"
  );
  const [amountInput, setAmountInput] = useState<string>(() =>
    (state.deposit_held_cents / 100).toFixed(2)
  );
  const [amountReason, setAmountReason] = useState("");
  const [internalNote, setInternalNote] = useState("");

  const deductionsCents = useMemo(
    () => state.deductions.reduce((sum, d) => sum + d.amount_cents, 0),
    [state.deductions]
  );

  // The saved refund is the server's. A draft line that has not been saved is
  // shown as a preview and labelled as one, so nobody mistakes it for the figure
  // the tenant will receive.
  const draftCents = draft ? (parseAmountToCents(draft.amount) ?? 0) : 0;
  const previewRefund = Math.max(
    0,
    state.deposit_held_cents - deductionsCents - draftCents
  );
  const hasUnsavedDraft = draftCents > 0;

  const shortfall = Math.max(
    0,
    state.deposit_expected_cents - state.deposit_held_cents
  );

  // Returns the fresh state on success (so a caller can read what changed —
  // the decision, the refund total) or null on failure, rather than a bare
  // boolean that would throw that information away.
  const run = async (
    fn: () => Promise<ReleaseReviewState>
  ): Promise<ReleaseReviewState | null> => {
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      onState(next);
      return next;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    const cents = parseAmountToCents(draft.amount);
    if (!cents) {
      setError(
        locale === "de" ? "Bitte einen Betrag eingeben." : "Enter an amount."
      );
      return;
    }
    if (draft.reason.trim().length < REASON_MIN_LENGTH) {
      setError(t("reasonTooShort"));
      return;
    }
    const payload = {
      category: draft.category,
      amountCents: cents,
      reason: draft.reason.trim(),
    };
    const wasEditing = !!editingId;
    const next = await run(() =>
      editingId
        ? updateDeduction(auth, editingId, payload)
        : addDeduction(auth, payload)
    );
    if (next) {
      onLog?.({
        kind: wasEditing ? "deduction_updated" : "deduction_added",
        category: payload.category,
        amountCents: payload.amountCents,
      });
      setDraft(null);
      setEditingId(null);
    }
  };

  /**
   * Release by typed amount. The withheld remainder becomes one real deduction
   * carrying the reason, so the tenant's statement still explains every euro —
   * which is why the reason is required rather than optional the moment the
   * refund is short of the deposit.
   */
  const submitAmount = async () => {
    const refundCents = parseAmountToCents(amountInput);
    // 0 is a legitimate refund (withhold everything) but parseAmountToCents
    // rejects it as an amount, so treat an explicit zero separately.
    const zero = /^0([.,]0{1,2})?$/.test(amountInput.trim());
    const cents = zero ? 0 : refundCents;
    if (cents === null) {
      setError(t("amountInvalid"));
      return;
    }
    if (cents > state.deposit_held_cents) {
      setError(t("amountTooHigh"));
      return;
    }
    if (
      cents < state.deposit_held_cents &&
      amountReason.trim().length < REASON_MIN_LENGTH
    ) {
      setError(t("reasonTooShort"));
      return;
    }
    const next = await run(() =>
      releaseDepositByAmount(auth, {
        refundCents: cents,
        reason: amountReason.trim() || undefined,
        note: internalNote.trim() || undefined,
      })
    );
    // This panel never collects a transfer proof (that is DepositRefundForm's
    // job), so a release through here never pays in the same call.
    if (next) {
      onLog?.({
        kind: "released_by_amount",
        refundCents: next.refund_cents,
        excessCents: next.excess_cents,
        paid: false,
      });
    }
  };

  const submit = async () => {
    if (!decision) return;
    if (decision === "partial" && state.deductions.length === 0) {
      setError(t("needLine"));
      return;
    }
    if (decision === "full" && state.deductions.length > 0) {
      setError(t("fullHasLines"));
      return;
    }
    const next = await run(() => releaseDeposit(auth, { decision }));
    if (next) {
      onLog?.({
        kind: "released",
        decision,
        refundCents: next.refund_cents,
        excessCents: next.excess_cents,
      });
    }
  };

  /* ── already decided ── */
  if (state.released_at) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ ...iconBox, background: "#DCFCE7", color: "#15803D" }}>
            ✓
          </span>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {t("doneTitle")}
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
              {t("doneBody")}
            </div>
          </div>
        </div>
        <div style={{ ...mathsBox, marginTop: 16 }}>
          <MathRow
            label={t("held")}
            value={money(state.deposit_held_cents, locale)}
          />
          {state.deductions.length > 0 && (
            <MathRow
              label={`${t("deductions")} · ${state.deductions.length}`}
              value={`−${money(deductionsCents, locale)}`}
              tone="deduct"
            />
          )}
          <MathRow
            label={t("refund")}
            value={money(state.refund_cents, locale)}
            total
          />
        </div>
        {state.released_by_name && (
          <div style={{ fontSize: 12.5, color: "#94A3B8", marginTop: 12 }}>
            {t("decidedBy")} {state.released_by_name} ·{" "}
            {dateStr(state.released_at, locale)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.015em" }}>
        {t("title")}
      </div>
      <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
        {[
          state.tenant_name,
          state.room_number
            ? locale === "de"
              ? `Zimmer ${state.room_number}`
              : `Room ${state.room_number}`
            : null,
          state.property_name,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
      <div style={{ fontSize: 12.5, color: "#94A3B8", marginTop: 2 }}>
        {t("tenancy")}: {dateStr(state.tenancy_start, locale)} –{" "}
        {dateStr(state.move_out_date, locale)}
      </div>

      <div
        style={{
          fontSize: 13,
          lineHeight: 1.55,
          color: "#475569",
          marginTop: 14,
          padding: "12px 14px",
          background: "#F8FAFC",
          border: "1px solid #ECEEF1",
          borderRadius: 12,
        }}
      >
        {t("intro")}
      </div>

      {/* the bundle */}
      {state.bundle_url && (
        <div
          style={{
            ...subCard,
            marginTop: 16,
            display: "flex",
            gap: 13,
            alignItems: "center",
          }}
        >
          <span style={{ ...iconBox, background: "#EEF2FF", color: "#4338CA" }}>
            🗂
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {t("bundleTitle")}
            </div>
            <div style={{ fontSize: 12.5, color: "#94A3B8", marginTop: 2 }}>
              {t("bundleSub")}
              {state.bundle_page_count ? ` · ${state.bundle_page_count}` : ""}
            </div>
          </div>
          <a
            href={state.bundle_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btn, textDecoration: "none", display: "inline-block" }}
          >
            {t("openBundle")}
          </a>
        </div>
      )}

      {/* deposit held */}
      <div style={{ ...mathsBox, marginTop: 14 }}>
        <MathRow
          label={t("agreed")}
          value={money(state.deposit_expected_cents, locale)}
        />
        <MathRow
          label={t("held")}
          value={money(state.deposit_held_cents, locale)}
          total
        />
      </div>

      {shortfall > 0 && (
        <Note tone="warn">
          <strong>{t("shortfallTitle")}</strong> — {money(shortfall, locale)}.{" "}
          {t("shortfall")}
        </Note>
      )}

      {/* decision */}
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 22 }}>
        {t("question")}
      </div>

      {/* Amount vs itemised. Amount is the default; itemised is there for the
          settlements that genuinely need line-by-line reasoning. */}
      <div style={modeSwitch}>
        <button
          type="button"
          disabled={busy}
          onClick={() => setMode("amount")}
          style={mode === "amount" ? modeTabOn : modeTab}
        >
          {t("modeAmount")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setMode("itemised")}
          style={mode === "itemised" ? modeTabOn : modeTab}
        >
          {t("modeItemised")}
        </button>
      </div>

      {mode === "amount" && (
        <AmountMode
          locale={locale}
          t={t}
          busy={busy}
          heldCents={state.deposit_held_cents}
          hasItemised={state.deductions.length > 0}
          amount={amountInput}
          onAmount={setAmountInput}
          reason={amountReason}
          onReason={setAmountReason}
          note={internalNote}
          onNote={setInternalNote}
          onSubmit={() => void submitAmount()}
        />
      )}

      {mode === "itemised" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginTop: 10,
          }}
        >
          <Choice
            on={decision === "full"}
            title={t("fullTitle")}
            sub={t("fullSub")}
            onClick={() => setDecision("full")}
            disabled={busy}
          />
          <Choice
            on={decision === "partial"}
            title={t("partialTitle")}
            sub={t("partialSub")}
            onClick={() => setDecision("partial")}
            disabled={busy}
          />
        </div>
      )}

      {mode === "itemised" && decision === "partial" && (
        <>
          {/* saved lines */}
          {state.deductions.length > 0 && (
            <div style={{ marginTop: 18 }}>
              {state.deductions.map((d) => (
                <div key={d.id} style={lineRow}>
                  <span>
                    <span
                      style={{
                        ...chip,
                        background: CATEGORY_STYLE[d.category].bg,
                        color: CATEGORY_STYLE[d.category].fg,
                      }}
                    >
                      {CATEGORY_COPY[d.category][locale]}
                    </span>
                  </span>
                  <span style={{ fontSize: 13, color: "#334155" }}>
                    {d.reason}
                  </span>
                  <span style={amountCell}>
                    −{money(d.amount_cents, locale)}
                  </span>
                  <button
                    type="button"
                    aria-label={t("remove")}
                    disabled={busy}
                    onClick={() =>
                      void run(() => deleteDeduction(auth, d.id)).then(
                        (next) =>
                          next &&
                          onLog?.({
                            kind: "deduction_deleted",
                            category: d.category,
                            amountCents: d.amount_cents,
                          })
                      )
                    }
                    style={removeBtn}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* the rent-ledger suggestion */}
          {state.suggested_unpaid_rent && !suggestionDismissed && !draft && (
            <div
              style={{
                ...subCard,
                marginTop: 12,
                background: "#F7F5FF",
                borderColor: "#ECE7FD",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6D28D9" }}>
                {t("suggestionTitle")}
              </div>
              <div style={{ fontSize: 13, color: "#334155", marginTop: 6 }}>
                {state.suggested_unpaid_rent.reason}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  disabled={busy}
                  style={{ ...btn, borderColor: "#D9D2F5" }}
                  onClick={() =>
                    setDraft({
                      category: "unpaid_rent",
                      reason: state.suggested_unpaid_rent!.reason,
                      amount: (
                        state.suggested_unpaid_rent!.amount_cents / 100
                      ).toFixed(2),
                    })
                  }
                >
                  {t("suggestionApply")}
                </button>
                <button
                  type="button"
                  style={{ ...btn, color: "#64748B" }}
                  onClick={() => setSuggestionDismissed(true)}
                >
                  {t("suggestionDismiss")}
                </button>
              </div>
            </div>
          )}

          {/* draft line */}
          {draft ? (
            <div style={{ ...subCard, marginTop: 12 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <label style={fieldLabel}>
                  {t("category")}
                  <select
                    value={draft.category}
                    disabled={busy}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        category: e.target.value as DeductionCategory,
                      })
                    }
                    style={input}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {CATEGORY_COPY[c][locale]}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ ...fieldLabel, width: 140 }}>
                  {t("amount")}
                  <input
                    value={draft.amount}
                    disabled={busy}
                    inputMode="decimal"
                    placeholder={locale === "de" ? "0,00" : "0.00"}
                    onChange={(e) =>
                      setDraft({ ...draft, amount: e.target.value })
                    }
                    style={{ ...input, textAlign: "right" }}
                  />
                </label>
              </div>
              <label style={{ ...fieldLabel, marginTop: 10 }}>
                {t("reason")}
                <textarea
                  value={draft.reason}
                  disabled={busy}
                  rows={3}
                  onChange={(e) =>
                    setDraft({ ...draft, reason: e.target.value })
                  }
                  style={{
                    ...input,
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
                <span
                  style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 4 }}
                >
                  {t("reasonHint")}
                </span>
              </label>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void saveDraft()}
                  style={{ ...btn, ...btnPrimary }}
                >
                  {busy ? t("submitting") : t("save")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setDraft(null);
                    setEditingId(null);
                    setError(null);
                  }}
                  style={{ ...btn, color: "#64748B" }}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setDraft(emptyDraft())}
              style={{ ...btn, marginTop: 12 }}
            >
              + {t("addLine")}
            </button>
          )}

          {/* maths */}
          <div style={{ ...mathsBox, marginTop: 16 }}>
            <MathRow
              label={t("held")}
              value={money(state.deposit_held_cents, locale)}
            />
            <MathRow
              label={`${t("deductions")} · ${state.deductions.length}${
                hasUnsavedDraft ? " +1" : ""
              }`}
              value={`−${money(deductionsCents + draftCents, locale)}`}
              tone="deduct"
            />
            <MathRow
              label={`${t("refund")}${hasUnsavedDraft ? ` (${t("preview")})` : ""}`}
              value={money(
                hasUnsavedDraft ? previewRefund : state.refund_cents,
                locale
              )}
              total
            />
          </div>

          {deductionsCents + draftCents > state.deposit_held_cents && (
            <Note tone="bad">
              <strong>{t("overTitle")}</strong> — {t("over")}
            </Note>
          )}
        </>
      )}

      {error && <Note tone="bad">{error}</Note>}

      {mode === "itemised" && decision && (
        <>
          <div
            style={{
              fontSize: 12.5,
              color: "#64748B",
              lineHeight: 1.5,
              marginTop: 18,
            }}
          >
            {t("consequence")}
          </div>
          <button
            type="button"
            disabled={
              busy || (decision === "partial" && !state.deductions.length)
            }
            onClick={() => void submit()}
            style={{
              ...btn,
              ...btnPrimary,
              marginTop: 12,
              opacity:
                busy || (decision === "partial" && !state.deductions.length)
                  ? 0.5
                  : 1,
            }}
          >
            {busy
              ? t("submitting")
              : decision === "full"
                ? t("submitFull")
                : `${t("submitPartial")} ${money(state.refund_cents, locale)}`}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * The one-number path: type the refund, say why the rest is withheld.
 *
 * The reason field is deliberately NOT the internal note. What is withheld has
 * to be explained to the tenant, and an amount saved with its explanation in an
 * admin-only field produces a statement with money missing and nothing to
 * justify it. So the two are separate fields with their visibility stated on
 * each, and this one is required the moment the refund is short.
 */
function AmountMode({
  locale,
  t,
  busy,
  heldCents,
  hasItemised,
  amount,
  onAmount,
  reason,
  onReason,
  note,
  onNote,
  onSubmit,
}: {
  locale: Locale;
  t: (key: keyof typeof COPY) => string;
  busy: boolean;
  heldCents: number;
  hasItemised: boolean;
  amount: string;
  onAmount: (v: string) => void;
  reason: string;
  onReason: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
  onSubmit: () => void;
}) {
  const zero = /^0([.,]0{1,2})?$/.test(amount.trim());
  const parsed = zero ? 0 : parseAmountToCents(amount);
  const refundCents = parsed ?? 0;
  const withheldCents =
    parsed === null ? 0 : Math.max(0, heldCents - refundCents);
  const overHeld = parsed !== null && refundCents > heldCents;
  const needsReason = parsed !== null && withheldCents > 0;

  if (hasItemised) {
    return <Note tone="warn">{t("hasItemised")}</Note>;
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={fieldCaption}>{t("refundAmount")}</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          marginTop: 8,
        }}
      >
        <div style={amountBox}>
          <span style={{ fontSize: 13, color: "#94A3B8", fontWeight: 700 }}>
            EUR
          </span>
          <input
            value={amount}
            disabled={busy}
            inputMode="decimal"
            aria-label={t("refundAmount")}
            onChange={(e) => onAmount(e.target.value)}
            style={amountInputStyle}
          />
        </div>
        <div style={{ fontSize: 13.5, color: "#64748B" }}>
          {t("ofTotal")}{" "}
          <strong style={{ color: "#0F172A" }}>
            {money(heldCents, locale)}
          </strong>{" "}
          {t("totalDeposit")}
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          disabled={busy}
          onClick={() => onAmount((heldCents / 100).toFixed(2))}
          style={smallBtn}
        >
          {t("fullAmountBtn")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onAmount("0.00")}
          style={smallBtn}
        >
          {t("withholdAllBtn")}
        </button>
      </div>

      {/* A bar that fills with what is being kept back, so the split is legible
          before reading either number. */}
      <div style={splitBar}>
        <div
          style={{
            width: `${heldCents > 0 ? Math.min(100, Math.max(0, (refundCents / heldCents) * 100)) : 0}%`,
            height: "100%",
            background: "#2DD4BF",
            borderRadius: 999,
          }}
        />
      </div>

      {overHeld && <Note tone="bad">{t("amountTooHigh")}</Note>}

      {withheldCents > 0 && !overHeld && (
        <div
          style={{
            fontSize: 12.5,
            color: "#B91C1C",
            fontWeight: 700,
            marginTop: 10,
          }}
        >
          {t("withheld")}: {money(withheldCents, locale)}
        </div>
      )}

      {/* Required as soon as anything is withheld — this text is what the tenant
          reads in place of the missing money. */}
      {needsReason && !overHeld && (
        <label style={{ ...fieldLabel, marginTop: 14 }}>
          {t("amountReason")}
          <textarea
            value={reason}
            disabled={busy}
            rows={3}
            aria-label={t("amountReason")}
            onChange={(e) => onReason(e.target.value)}
            style={{ ...input, resize: "vertical", fontFamily: "inherit" }}
          />
          <span style={{ fontSize: 11.5, color: "#0F5C52", marginTop: 4 }}>
            {t("amountReasonVisible")}
          </span>
        </label>
      )}

      <label style={{ ...fieldLabel, marginTop: 14 }}>
        {t("internalNote")}
        <textarea
          value={note}
          disabled={busy}
          rows={2}
          aria-label={t("internalNote")}
          onChange={(e) => onNote(e.target.value)}
          style={{ ...input, resize: "vertical", fontFamily: "inherit" }}
        />
        <span style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 4 }}>
          {t("internalNoteHint")}
        </span>
      </label>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginTop: 16,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          disabled={busy || overHeld || parsed === null}
          onClick={onSubmit}
          style={{
            ...btn,
            ...btnPrimary,
            opacity: busy || overHeld || parsed === null ? 0.5 : 1,
          }}
        >
          {busy ? t("submitting") : t("confirmAmount")}
        </button>
        <span style={{ fontSize: 12.5, color: "#94A3B8" }}>
          {t("documentsOnly")}
        </span>
      </div>
    </div>
  );
}

/* ── small pieces ─────────────────────────────────────────────────────────── */

function MathRow({
  label,
  value,
  tone,
  total,
}: {
  label: string;
  value: string;
  tone?: "deduct";
  total?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        fontSize: total ? 15 : 13,
        borderTop: total ? "1px solid #E2E7EC" : undefined,
        paddingTop: total ? 10 : undefined,
      }}
    >
      <span style={{ color: total ? "#0F172A" : "#64748B", fontWeight: 700 }}>
        {label}
      </span>
      <span
        style={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          fontSize: total ? 21 : undefined,
          color: total ? "#0F5C52" : tone === "deduct" ? "#B91C1C" : "#0F172A",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Choice({
  on,
  title,
  sub,
  onClick,
  disabled,
}: {
  on: boolean;
  title: string;
  sub: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        textAlign: "left",
        background: on ? "#F6FBFA" : "#fff",
        border: `2px solid ${on ? "#0F5C52" : "#ECEEF1"}`,
        borderRadius: 16,
        padding: "14px 16px",
        cursor: disabled ? "default" : "pointer",
        font: "inherit",
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 5 }}>{sub}</div>
    </button>
  );
}

function Note({
  tone,
  children,
}: {
  tone: "warn" | "bad";
  children: React.ReactNode;
}) {
  const palette =
    tone === "warn"
      ? { bg: "#FFFBEB", border: "#FDE68A", fg: "#92400E" }
      : { bg: "#FEF2F2", border: "#FECACA", fg: "#B91C1C" };
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        borderRadius: 13,
        padding: "12px 14px",
        fontSize: 12.5,
        lineHeight: 1.5,
        marginTop: 12,
      }}
    >
      {children}
    </div>
  );
}

/* ── styles ───────────────────────────────────────────────────────────────── */

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #ECEEF1",
  borderRadius: 20,
  padding: "22px 24px",
  color: "#0F172A",
};

const subCard: React.CSSProperties = {
  background: "#F8FAFB",
  border: "1px solid #ECEEF1",
  borderRadius: 14,
  padding: "14px 16px",
};

const mathsBox: React.CSSProperties = {
  ...subCard,
  display: "flex",
  flexDirection: "column",
  gap: 9,
};

const iconBox: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 17,
  fontWeight: 700,
};

const btn: React.CSSProperties = {
  border: "1px solid #E7E9EC",
  background: "#fff",
  color: "#6D28D9",
  borderRadius: 11,
  padding: "10px 16px",
  fontSize: 13.5,
  fontWeight: 700,
  cursor: "pointer",
  font: "inherit",
};

const btnPrimary: React.CSSProperties = {
  background: "#0F5C52",
  borderColor: "#0F5C52",
  color: "#fff",
};

const lineRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "132px 1fr 108px 34px",
  gap: 12,
  alignItems: "start",
  padding: "13px 0",
  borderTop: "1px solid #F1F3F5",
};

const chip: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  borderRadius: 8,
  padding: "5px 9px",
  display: "inline-block",
};

const amountCell: React.CSSProperties = {
  textAlign: "right",
  fontSize: 14,
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  color: "#B91C1C",
};

const removeBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  color: "#CBD5E1",
  fontSize: 18,
  cursor: "pointer",
  padding: "2px 6px",
  borderRadius: 6,
};

const fieldLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  fontSize: 12,
  fontWeight: 700,
  color: "#64748B",
};

const modeSwitch: React.CSSProperties = {
  display: "flex",
  gap: 4,
  background: "#F4F6F7",
  borderRadius: 12,
  padding: 4,
  marginTop: 10,
  width: "fit-content",
};

const modeTab: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "#64748B",
  borderRadius: 9,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  font: "inherit",
};

const modeTabOn: React.CSSProperties = {
  ...modeTab,
  background: "#fff",
  color: "#0F172A",
  fontWeight: 700,
  boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
};

const amountBox: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid #E2E7EC",
  borderRadius: 12,
  padding: "10px 14px",
  background: "#fff",
};

const amountInputStyle: React.CSSProperties = {
  border: "none",
  outline: "none",
  width: 96,
  fontSize: 19,
  fontWeight: 700,
  color: "#0F172A",
  fontVariantNumeric: "tabular-nums",
  background: "transparent",
  font: "inherit",
};

const splitBar: React.CSSProperties = {
  height: 5,
  borderRadius: 999,
  background: "#FEE2E2",
  overflow: "hidden",
  marginTop: 14,
};

const smallBtn: React.CSSProperties = {
  border: "1px solid #E7E9EC",
  background: "#fff",
  color: "#334155",
  borderRadius: 10,
  padding: "8px 14px",
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
  font: "inherit",
};

const fieldCaption: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#94A3B8",
};

const input: React.CSSProperties = {
  border: "1px solid #E2E7EC",
  borderRadius: 10,
  padding: "9px 11px",
  fontSize: 13.5,
  color: "#0F172A",
  background: "#fff",
  fontWeight: 500,
};
